import { Button, Grid, Icon, Typography } from 'gu-ui-kit'
import styles from 'modules/Stories/Stories.module.scss'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import clsx from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

const AUTOPLAY_INTERVAL = 10000

const GAP = 30

const SPLIDE_DIRECTION = {
  next: '>',
  prev: '<',
}

const EVENT_TYPES = {
  viewed: 'viewed',
  unliked: 'unliked',
  liked: 'liked',
  button_clicked: 'button_clicked',
}

const SPLIDE_OPTIONS = {
  rewind: false,
  perPage: 1,
  perMove: 1,
  arrows: false,
  pagination: false,
  drag: false,
  hasTrack: false,
  autoplay: false,
  gap: GAP,
}

export const Stories = ({ startIndex = 0, closeModal, sendEvent }) => {
  const breakpoint = useBreakpoint()
  const { data: storiesData = [] } = useSelector(({ stories }) => stories)

  const innerSplideRef = useRef([])
  const outerSplideRef = useRef(null)
  const timeout = useRef(null)

  const [prevArrowDisabled, setPrevArrowDisabled] = useState(false)
  const [nextArrowDisabled, setNextArrowDisabled] = useState(false)

  useEffect(() => {
    startTimer()
    setEvent(EVENT_TYPES.viewed)
    checkArrows()

    return () => {
      clearTimeout(timeout.current)
    }
  }, [])

  const startTimer = useCallback(() => {
    clearTimeout(timeout.current)

    timeout.current = setTimeout(() => {
      moveSplide('next')
    }, AUTOPLAY_INTERVAL)
  }, [timeout])

  const moveSplide = useCallback((direction = 'next') => {
    const { outerIndex, innerIndex } = getIndexes()

    const outerSplide = outerSplideRef.current.splide
    const innerSplide = innerSplideRef.current[outerIndex].splide
    const innerItemsLength = innerSplide.length
    const outerSplideLength = outerSplide.length
    if (direction === 'next' ? innerIndex < innerItemsLength - 1 : innerIndex > 0) {
      innerSplide.go(SPLIDE_DIRECTION[direction])
    } else {
      if (outerSplideLength - 1 === outerIndex && direction === 'next') {
        closeModal()
      } else {
        outerSplide.go(SPLIDE_DIRECTION[direction])
      }
    }
  }, [])

  const setEvent = useCallback(
    (event = EVENT_TYPES.viewed) => {
      const { outerIndex, innerIndex } = getIndexes()

      sendEvent({
        outerIndex,
        innerIndex,
        event,
        storyId: storiesData[outerIndex].stories[innerIndex].id,
      })
    },
    [storiesData, outerSplideRef, innerSplideRef],
  )

  const handleMoved = useCallback(() => {
    setEvent(EVENT_TYPES.viewed)
    startTimer()
    checkArrows()
  }, [outerSplideRef, innerSplideRef, storiesData])

  const checkArrows = useCallback(() => {
    const { outerIndex, innerIndex } = getIndexes()

    setPrevArrowDisabled(!outerIndex && !innerIndex)
    setNextArrowDisabled(
      !storiesData.length ||
        (outerIndex === storiesData.length - 1 &&
          innerIndex === storiesData[outerIndex].stories.length - 1),
    )
  }, [storiesData])

  const handleSplideClick = useCallback(
    (Splide, SplideSlide, e) => {
      const { innerWidth, innerHeight } = window
      const { clientX, clientY } = e

      if (breakpoint.fb || clientY < 92 || clientY > innerHeight - 92) {
        return
      }

      moveSplide(clientX < innerWidth / 2 ? 'prev' : 'next')
    },
    [!breakpoint.fb],
  )

  const getIndexes = useCallback(() => {
    const outerIndex = outerSplideRef.current.splide.index
    const innerIndex = innerSplideRef.current[outerIndex].splide.index
    return {
      outerIndex,
      innerIndex,
    }
  }, [outerSplideRef, innerSplideRef])

  const handleButtonClick = useCallback(() => {
    setEvent(EVENT_TYPES.button_clicked)
  }, [])

  useEffect(() => {
    const handleKeyUp = (e) => {
      if (e.keyCode === 39) {
        moveSplide('next')
      } else if (e.keyCode === 37) {
        moveSplide('prev')
      }
    }

    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  return (
    <>
      <Button
        className={styles['stories__modal-close-button']}
        onClick={closeModal}
        noLabel
        variant={'primary'}
        opacity={20}
        size={'m'}
        color={'Interface-Onsurface'}
        colorHover={'Interface-Eleven'}
        bgColorHover={'Interface-Onsurface'}
        startIcon={
          <Icon
            name={'close'}
            color={'inherit'}
          />
        }
      />
      <Splide
        ref={outerSplideRef}
        className={styles['stories__splide']}
        options={{
          ...SPLIDE_OPTIONS,
          drag: !breakpoint.fb,
          start: startIndex,
        }}
        hasTrack={false}
        onMoved={handleMoved}
      >
        <SplideTrack className={styles['stories__splide-track']}>
          {storiesData.map(({ id, stories }, outerIndex) => (
            <SplideSlide
              key={id}
              className={styles['stories__splide-slide']}
            >
              <Splide
                className={clsx(styles['stories__splide'], styles['stories__splide_inner'])}
                hasTrack={false}
                options={{
                  ...SPLIDE_OPTIONS,
                  pagination: true,
                  lazyLoad: true,
                  type: 'fade',
                  classes: {
                    page: clsx('splide__pagination__page', styles['stories__splide-page']),
                  },
                }}
                onMoved={handleMoved}
                onClick={handleSplideClick}
                ref={(el) => (innerSplideRef.current[outerIndex] = el)}
                style={{ '--interval': `${AUTOPLAY_INTERVAL}ms` }}
              >
                <SplideTrack className={styles['stories__splide-track']}>
                  {stories.map(
                    ({
                      id,
                      title = null,
                      image_src,
                      liked = false,
                      description = null,
                      button = null,
                    }) => (
                      <SplideSlide
                        key={id}
                        className={clsx(
                          styles['stories__splide-slide'],
                          styles['stories__splide-slide_inner'],
                        )}
                      >
                        <img
                          className={styles['stories__modal-image']}
                          data-splide-lazy={image_src}
                          alt={title}
                        />
                        <Grid
                          direction={'column'}
                          justifyContent={'end'}
                          alignItems={'start'}
                          wrap={false}
                          gap={16}
                          fluid={false}
                          className={styles['stories__modal-content']}
                        >
                          {title && (
                            <Typography
                              noMargin
                              variant={'Header-60'}
                              color={'Label-Onsurface'}
                            >
                              {title}
                            </Typography>
                          )}
                          {description && (
                            <Typography
                              noMargin
                              variant={'Body-Accent-20'}
                              color={'Label-Onsurface'}
                            >
                              {description}
                            </Typography>
                          )}
                        </Grid>

                        <Grid
                          className={styles['stories__modal-footer']}
                          justifyContent={'between'}
                          alignItems={'center'}
                          fluid={false}
                        >
                          <Button
                            noLabel
                            variant={'primary'}
                            opacity={20}
                            size={'m'}
                            onClick={() =>
                              setEvent(liked ? EVENT_TYPES.unliked : EVENT_TYPES.liked)
                            }
                            color={'Interface-Onsurface'}
                            colorHover={'Interface-Eleven'}
                            bgColorHover={'Interface-Onsurface'}
                            startIcon={
                              <Icon
                                name={liked ? 'bold-heart' : 'heart'}
                                color={liked ? 'Label-Error-Primary' : 'inherit'}
                              />
                            }
                          />
                          {button && (
                            <Button
                              component={'a'}
                              onClick={handleButtonClick}
                              target={'_blank'}
                              href={button.url}
                              variant={'primary'}
                              opacity={20}
                              size={'m'}
                              color={'Interface-Onsurface'}
                              colorHover={'Interface-Eleven'}
                              bgColorHover={'Interface-Onsurface'}
                            >
                              {button.text}
                            </Button>
                          )}
                        </Grid>
                      </SplideSlide>
                    ),
                  )}
                </SplideTrack>
                <ul className={clsx('splide__pagination', styles['stories__splide-pagination'])} />
              </Splide>
            </SplideSlide>
          ))}
        </SplideTrack>
        {!breakpoint.fb ? null : (
          <Grid
            justifyContent={'between'}
            fluid={false}
            className={styles['stories__splide-arrows']}
          >
            <Button
              variant={'ghost'}
              grey
              size={'m'}
              noLabel
              onClick={() => moveSplide('prev')}
              disabled={prevArrowDisabled}
              startIcon={
                <Icon
                  name={'arrow-line-left'}
                  color={'Interface-Eleven'}
                />
              }
            />
            <Button
              variant={'ghost'}
              grey
              size={'m'}
              noLabel
              onClick={() => moveSplide('next')}
              disabled={nextArrowDisabled}
              startIcon={
                <Icon
                  name={'arrow-line-right'}
                  color={'Interface-Eleven'}
                />
              }
            />
          </Grid>
        )}
      </Splide>
    </>
  )
}

Stories.propTypes = {
  startIndex: PropTypes.number,
  closeModal: PropTypes.func,
  sendEvent: PropTypes.func,
}
